/**
 * TreePicker — one node, chosen out of the hierarchy (`KLQ-20260905-002` §B).
 *
 * **What it replaced.** Four copies of the same trick: a `<select>` whose `<option>` labels
 * were indented by repeating a space `depth * 2` times — in `StructureDrawers`,
 * `GenerateDrawers`, `TaskCreateDrawer` and `ProjectShapeEditor`, three of them with a
 * non-breaking space and one with an ordinary one, which is what four copies of an idiom look
 * like. Leading spaces in an option label collapse in some browsers, carry no structure a
 * screen reader can announce (every option is a sibling, at one level, forever), and cannot
 * be collapsed — so a project with two hundred levels was a two-hundred-line flat menu.
 * `guard.mjs` check 5 refuses a fifth copy.
 *
 * It is `TreeTable`'s sibling, not a second tree: the walk is the same `lines()`, the twisty
 * is the same glyph with the same `treeTable.expand`/`treeTable.collapse` label, the indent
 * is the same 16px per depth as the row's own padding, and expansion is tracked as what is
 * *closed* (`TreeTable` rule 4) so a branch that arrives late is visible rather than hidden
 * behind a set nobody added it to.
 *
 * **Inline, never a popup.** It renders where the `<select>` stood, inside the drawer, so the
 * drawer's own scroll is the picker's scroll and there is no second layer to position, trap
 * or dismiss. The list is bounded in height rather than the tree's full depth.
 *
 * `role="tree"` is claimed here — unlike `TreeTable`, which deliberately does not claim
 * `treegrid` — because the arrow-key navigation the role promises is implemented below.
 *
 * No search box: the four `<select>`s it replaced had none, and a filter over a tree is a
 * second design question (which ancestors of a match stay visible?) that nothing yet asks.
 */

import { useEffect, useId, useRef, useState } from "react";
import type { KeyboardEvent } from "react";


import { useMessages } from "@/lib/i18n";
import { Twisty, TwistySpacer } from "@/components/Twisty";

import { lines } from "./lines";
import type { TreeTableNode } from "./TreeTable";
import styles from "./TreePicker.module.css";

export interface TreePickerProps<Row> {
  /** The picker's accessible name, in the consumer's words. Rendered above the tree. */
  readonly label: string;
  readonly nodes: readonly TreeTableNode<Row>[];
  /** The chosen node's id, or `null` for "none / the root". */
  readonly value: string | null;
  readonly onChange: (id: string | null) => void;
  /** The row's name. Rendered, and used as the twisty's accessible label. */
  readonly rowLabel: (row: Row) => string;
  /** A second, dimmer value at the row's end — a code, a count. Omitted when `""`. */
  readonly meta?: (row: Row) => string;
  /** A row that may be shown but not chosen (a move's own subtree). Default: none. */
  readonly disabled?: (row: Row) => boolean;
  /** When given, a first row meaning `null` — "the project root", "no location". */
  readonly noneLabel?: string;
  /** Shown as a selected row when `value` is not in `nodes` — see `TaskCreateDrawer`. */
  readonly unknownLabel?: string;
  /** The whole picker is inert — a submit is in flight. */
  readonly disabledAll?: boolean;
}

/**
 * The two rows that are not nodes. A node id can never collide with them: the server's ids
 * are UUIDs and these are not.
 *
 * Written as an escape rather than as the byte itself. A literal NUL in the source makes
 * git read the whole file as binary — no diff, no blame, no three-way merge — which is a
 * high price for a sentinel that reads the same either way.
 */
const NONE_KEY = "\u0000none";
const UNKNOWN_KEY = "\u0000unknown";

/** One drawn row — a node, the `noneLabel` row, or the `unknownLabel` row. */
interface PickerRow {
  /** React key and expansion identity; a node's own id for a node row. */
  readonly key: string;
  /** What `onChange` is called with when this row is chosen. */
  readonly id: string | null;
  readonly depth: number;
  readonly label: string;
  readonly meta: string;
  readonly expandable: boolean;
  readonly open: boolean;
  readonly disabled: boolean;
}

export function TreePicker<Row>({
  label,
  nodes,
  value,
  onChange,
  rowLabel,
  meta,
  disabled,
  noneLabel,
  unknownLabel,
  disabledAll = false,
}: TreePickerProps<Row>) {
  const t = useMessages("app");
  const labelId = useId();
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  /** Which row the roving `tabIndex` sits on, once somebody has moved it. */
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const rowRefs = useRef(new Map<string, HTMLDivElement>());
  const treeRef = useRef<HTMLDivElement>(null);
  /** The chosen row is revealed once, when the picker first draws it. */
  const revealed = useRef(false);

  const drawn = lines(nodes, "grouped", collapsed);

  const rows: PickerRow[] = [];
  if (noneLabel !== undefined)
    rows.push({
      key: NONE_KEY,
      id: null,
      depth: 0,
      label: noneLabel,
      meta: "",
      expandable: false,
      open: false,
      disabled: false,
    });
  for (const line of drawn)
    rows.push({
      key: line.id,
      id: line.id,
      depth: line.depth,
      label: rowLabel(line.row),
      meta: meta?.(line.row) ?? "",
      expandable: line.expandable,
      open: line.open,
      disabled: disabled?.(line.row) ?? false,
    });
  // The pre-filled node may not be in this tree at all — see `TaskCreateDrawer`, where the
  // branch can be removed between the rail publishing a link and somebody opening it. The
  // value stays chosen and goes to the server, whose answer about it is the real one.
  if (
    unknownLabel !== undefined &&
    value !== null &&
    !drawn.some((line) => line.id === value)
  )
    rows.push({
      key: UNKNOWN_KEY,
      id: value,
      depth: 0,
      label: unknownLabel,
      meta: "",
      expandable: false,
      open: false,
      disabled: false,
    });

  /** Roving `tabIndex`: where focus has been left, else the chosen row, else the first. */
  const active =
    (rows.some((row) => row.key === activeKey) ? activeKey : null) ??
    rows.find((row) => row.id === value)?.key ??
    rows[0]?.key ??
    null;

  /**
   * Show the chosen row, once, when the picker first draws it.
   *
   * The `<select>` this replaced did it without being asked: whatever was chosen was the thing
   * on screen. Here the list is a bounded box (`TreePicker.module.css`), and the value is
   * usually pre-filled — `TaskCreateDrawer` opens with the node the rail linked from — so
   * without this the drawer opens scrolled to the top of a tree whose chosen row is somewhere
   * below the fold. The picker scrolls its own box rather than calling `scrollIntoView`, which
   * would drag the drawer around it too; and only on the first draw, so choosing a row later
   * never moves the list out from under the pointer.
   */
  useEffect(() => {
    const tree = treeRef.current;
    const chosen = rows.find((row) => row.id === value);
    if (revealed.current || tree === null || chosen === undefined) return;
    const element = rowRefs.current.get(chosen.key);
    if (element === undefined) return;
    revealed.current = true;
    const above = element.offsetTop;
    const below = above + element.offsetHeight - tree.clientHeight;
    if (above < tree.scrollTop) tree.scrollTop = above;
    else if (below > tree.scrollTop) tree.scrollTop = below;
  });

  const toggle = (key: string): void => {
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const setOpen = (key: string, open: boolean): void => {
    setCollapsed((current) => {
      const next = new Set(current);
      if (open) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  /** The next row in `delta`'s direction that can be focused, from `from` exclusive. */
  const step = (from: number, delta: number): void => {
    for (let index = from + delta; index >= 0 && index < rows.length; index += delta) {
      const row = rows[index];
      if (row === undefined || row.disabled) continue;
      rowRefs.current.get(row.key)?.focus();
      return;
    }
  };

  const choose = (row: PickerRow): void => {
    if (disabledAll || row.disabled) return;
    onChange(row.id);
  };

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
    const index = rows.findIndex((row) => row.key === active);
    const row = rows[index];
    if (row === undefined) return;
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        step(index, 1);
        break;
      case "ArrowUp":
        event.preventDefault();
        step(index, -1);
        break;
      case "Home":
        event.preventDefault();
        step(-1, 1);
        break;
      case "End":
        event.preventDefault();
        step(rows.length, -1);
        break;
      case "ArrowRight":
        event.preventDefault();
        // Open a closed branch, then move into it — the tree pattern's one key that does
        // two different things depending on where it lands.
        if (row.expandable && !row.open) setOpen(row.key, true);
        else if (row.expandable) step(index, 1);
        break;
      case "ArrowLeft":
        event.preventDefault();
        if (row.expandable && row.open) setOpen(row.key, false);
        else
          for (let above = index - 1; above >= 0; above -= 1) {
            const parent = rows[above];
            if (parent === undefined || parent.depth >= row.depth) continue;
            rowRefs.current.get(parent.key)?.focus();
            break;
          }
        break;
      case "Enter":
      case " ":
        event.preventDefault();
        choose(row);
        break;
      default:
        break;
    }
  }

  return (
    <div className={styles.field}>
      <span className={styles.label} id={labelId}>
        {label}
      </span>
      <div
        ref={treeRef}
        role="tree"
        aria-labelledby={labelId}
        aria-disabled={disabledAll || undefined}
        className={styles.tree}
        onKeyDown={handleKeyDown}
      >
        {rows.map((row) => {
          return (
            <div
              key={row.key}
              ref={(element) => {
                if (element === null) rowRefs.current.delete(row.key);
                else rowRefs.current.set(row.key, element);
              }}
              role="treeitem"
              aria-level={row.depth + 1}
              aria-selected={row.id === value}
              aria-expanded={row.expandable ? row.open : undefined}
              aria-disabled={disabledAll || row.disabled || undefined}
              tabIndex={row.key === active ? 0 : -1}
              className={styles.row}
              // The indent is the row's own padding rather than a spacer element, so a long
              // name wraps against the text edge instead of under the twisty.
              style={{ paddingInlineStart: `${String(8 + row.depth * 16)}px` }}
              onFocus={() => {
                setActiveKey(row.key);
              }}
              onClick={() => {
                choose(row);
              }}
            >
              {/* The shared control (`components/Twisty`). `tabbable={false}` because the
                  rows are the tab stops here; the stop-the-press-becoming-a-choice rule
                  lives in the component now, not at this call site. */}
              {row.expandable ? (
                <Twisty
                  tabbable={false}
                  open={row.open}
                  label={t(
                    row.open ? "treeTable.collapse" : "treeTable.expand",
                    { name: row.label },
                  )}
                  onToggle={() => {
                    toggle(row.key);
                  }}
                />
              ) : (
                <TwistySpacer />
              )}
              <span className={styles.name}>{row.label}</span>
              {row.meta === "" ? null : (
                <span className={styles.meta}>{row.meta}</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
